import { createContext, useState, useEffect, useContext } from "react"
import { useGlobalContext, decryptUsername } from "./GlobalContext"
import { decrypt } from "./CryptoUtil"

const LeaderboardContext = createContext()

export const LeaderboardProviderRainOfWins = ({ children }) => {
  const { username } = useGlobalContext()
  const [leaderboardData, setLeaderboardData] = useState([])
  const [loadingBoard, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [playerPosition, setPlayerPosition] = useState(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const DEBUG = false

  useEffect(() => {
    const loggedInState = localStorage.getItem("isLoggedIn") === "true"
    setIsLoggedIn(loggedInState)
  }, [])

  const getBonusByRank = (rank) => {
    const r = parseInt(rank, 10)
    if (r === 1) return 500
    if (r === 2) return 250
    if (r === 3) return 150
    if (r >= 4 && r <= 15) return 70
    if (r >= 16 && r <= 25) return 50

    return 0
  }

  const maskUsernameExactKeepLast = (u) => {
    if (!u) return u

    const normalized = (u + "###########").slice(0, 11)
    const lastChar = u.slice(-2)

    return normalized.slice(0, 5) + "**" + normalized.charAt(7) + "*" + lastChar
  }

  const getPlayerPosition = () => {
    const storedUsername = decryptUsername(localStorage.getItem("username"))
    if (!storedUsername) return null

    if (DEBUG) {
      console.log("[DEBUG] Matching storedUsername:", storedUsername)
      console.log(
        "[DEBUG] Againts leaderboard randomChars:",
        leaderboardData.map((e) => e.randomChars)
      )
    }

    const found = leaderboardData.find(
      (entry) => entry.randomChars === storedUsername
    )

    return found ? parseInt(found.ranking, 10) : null
  }

  useEffect(() => {
    const pos = getPlayerPosition()
    if (pos !== null) setPlayerPosition(pos)
  }, [leaderboardData])

  useEffect(() => {
    if (!isLoggedIn) return
    const fetchLeaderboardData = async () => {
      try {
        setLoading(true)
        const url = `${process.env.REACT_APP_API_JSON_URL_PROMO}?`
        if (DEBUG) console.log("[DEBUG] Fetching leaderboard from:", url)

        const response = await fetch(url)
        if (!response.ok) throw new Error(`HTTP error: ${response.status}`)

        const rawData = await response.json()
        if (DEBUG) console.log("[DEBUG] Raw leaderboard data:", rawData)

        const parsedData = await Promise.all(
          Object.entries(rawData).map(async ([ranking, value]) => {
            const parts = value.split("|")
            const username = parts[0]?.trim()
            const freespins = getBonusByRank(ranking)

            const encryptedRandom = parts[2]?.trim() || ""

            let randomChars = encryptedRandom
            let masked = encryptedRandom

            try {
              const decrypted = await decrypt(encryptedRandom)
              randomChars = decrypted
              masked = maskUsernameExactKeepLast(decrypted)
            } catch (e) {
              console.error("Decrypt error:", e)
            }

            if (DEBUG) {
              console.log(`[DEBUG] Rank ${ranking}:`, {
                username,
                freespins,
                randomChars,
                masked,
              })
            }

            return { ranking, username, freespins, randomChars, masked }
          })
        )

        if (DEBUG) console.log("[DEBUG] Parsed leaderboard data:", parsedData)
        setLeaderboardData(parsedData.slice(0, 25))
      } catch (err) {
        console.error("Leaderboard API error:", err)
        setError(err)
      } finally {
        setLoading(false)
      }
    }
    fetchLeaderboardData()
  }, [isLoggedIn])

  return (
    <>
      <LeaderboardContext.Provider
        value={{
          leaderboardData,
          loadingBoard,
          error,
          getBonusByRank,
          getPlayerPosition,
          playerPosition,
        }}
      >
        {children}
      </LeaderboardContext.Provider>
    </>
  )
}

export const useLeaderboard = () => useContext(LeaderboardContext)
